import App from 'app/app.js';
import 'app/service/StorageService.js';
import 'app/action/SessionTimeoutAction.js';
import 'app/action/LogoutSuccessAction.js';
import 'app/action/DeserializeAction.js';
import { ReplaySubject } from 'rxjs';

function SubjectStore(
  $filter,
  StorageService,
  SessionTimeoutAction,
  LogoutSuccessAction,
  DeserializeAction,
  $injector,
  $q,
  LooksStore,
  JobStore,
  GuidGenerator,
  MasterSubjectUserProfileXrefStatusConstants,
  MasterEventConsolidationService
) {
  let studentPlaceholderCount = 0;

  let selectedAccount;
  let selectedSubject;
  let verifiedSubjects = [];
  let subjectsByJob = [];
  let foundMatchSubject;
  let selectedMasterEvent = {};
  let fromConfirmExistingStudent = false;
  let subjectBannerCollapsed = true;
  let subjectAvatarCollapsed = true;
  let temporaryAccountAlias;

  let subjectsLoading$ = new ReplaySubject();

  this.getSubjectsLoading$ = () => subjectsLoading$.asObservable();

  this.subjectsLoadingSuccess = () => subjectsLoading$.next(undefined);
  this.subjectsLoadingFailure = (error) => subjectsLoading$.error(error);

  this.setSubjectBannerCollapse = function (flag) {
    subjectBannerCollapsed = flag;
  };

  this.getSubjectBannerCollapse = function () {
    return subjectBannerCollapsed;
  };

  this.setSubjectAvatarsCollapse = function (flag) {
    subjectAvatarCollapsed = flag;
  };

  this.getSubjectAvatarsCollapse = function () {
    return subjectAvatarCollapsed;
  };

  this.getSelectedMasterEvent = function () {
    return selectedMasterEvent;
  };

  this.setSelectedMasterEvent = function (event) {
    selectedMasterEvent = event;
  };

  /**
   * Assigns each subject's `selectedEvent` to a matching event for the given conditions
   * (schoolYearDisplay + sellingMethodName + subProgramName).
   *
   * Resolution order per subject:
   * 1. `currentEvent.event` already matches — no change.
   * 2. A matching event exists in `masterEvents` — use it as `selectedEvent`.
   * 3. No match — keep `selectedEvent` but null out `subjectInfo.previewImageUrl`
   *    and `bearerTokenURL` to prevent stale portrait rendering.
   *
   * No-op when `conditions` is falsy. Does not mutate the source event object.
   */
  this.setRelatedMasterEventToAllSubjects = function (subjects, conditions) {
    if (!conditions) {
      return;
    }

    const schoolYearDisplay = conditions.schoolYearDisplay;
    const sellingMethodName = conditions.sellingMethodName;
    const subProgramName = conditions.subProgramName;

    const matchesCriteria = (event) => {
      if (!event) return false;
      const eventSellingMethodName = event.eventType?.sellingMethodName || event.sellingMethod;
      const eventSubProgramName = event.eventType?.subProgramName || event.jobName;
      return (
        event.schoolYearDisplay === schoolYearDisplay &&
        eventSellingMethodName?.toUpperCase() === sellingMethodName &&
        eventSubProgramName?.toUpperCase() === subProgramName
      );
    };

    subjects.forEach((subject) => {
      if (matchesCriteria(subject.currentEvent?.event)) {
        return;
      }

      const relatedEvent = subject.masterEvents?.find((event) => matchesCriteria(event));
      if (relatedEvent) {
        subject.selectedEvent = relatedEvent;
        return;
      }
      // No related event of this subject's own: fall back to the selected
      // subject's event for its scheduling/program metadata (schoolYear,
      // sellingMethod, jobNumber, portraitIdAccessCode, name/grade…), but strip
      // the image identity. previewImageUrl/bearerTokenURL in subjectInfo are
      // per-subject; leaving them makes downstream image resolution
      // (SubjectLookResolver, and the copy-order modal via student-item) render
      // the SELECTED subject's portrait for every subject lacking a match.
      if (!subject.selectedEvent) {
        return;
      }
      const fallbackEvent = { ...subject.selectedEvent };
      if (fallbackEvent.subjectInfo) {
        fallbackEvent.subjectInfo = {
          ...fallbackEvent.subjectInfo,
          previewImageUrl: null,
          bearerTokenURL: null
        };
      }
      subject.selectedEvent = fallbackEvent;
    });
  };

  this.getFromConfirmExistingStudent = function () {
    return fromConfirmExistingStudent;
  };

  this.setFromConfirmExistingStudent = function (flag) {
    fromConfirmExistingStudent = flag;
  };

  this.getSubjectGrade = function (subject) {
    if (subject && subject.currentGrade) {
      return subject.currentGrade;
    }
    if (subject && subject.subjectGrade) {
      return subject.subjectGrade;
    }
    if (selectedSubject) {
      if (selectedSubject.currentGrade) {
        return selectedSubject.currentGrade;
      }
      var hasSubjectInfo =
        selectedSubject.masterEvents && selectedSubject.masterEvents[0] && selectedSubject.masterEvents[0].subjectInfo;
      if (!selectedSubject.subjectGrade && hasSubjectInfo) {
        return selectedSubject.masterEvents[0].subjectInfo.grade;
      }
      return selectedSubject.subjectGrade;
    }
  };

  this.updateAccountInfo = function (schoolName, masterSubjectId) {
    // Check for selectedSubject existence
    if (selectedSubject) {
      if (selectedSubject.account && selectedSubject.account.accountAlias) {
        selectedSubject.account.accountAlias = schoolName;
      }
      if (selectedSubject.selectedEvent && selectedSubject.selectedEvent.accountName) {
        selectedSubject.selectedEvent.accountName = schoolName;
      }
      if (
        selectedSubject.currentEvent &&
        selectedSubject.currentEvent.event &&
        selectedSubject.currentEvent.event.accountName
      ) {
        selectedSubject.currentEvent.event.accountName = schoolName;
      }
      var subject = this.getVerifiedSubjectByMasterSubjectId(masterSubjectId);

      if (subject && subject.updatedAccount) {
        subject.updatedAccount.accountName = schoolName;
      } else {
        subject.updatedAccount = { accountName: schoolName };
      }
    }
    this.updateStorage();
  };

  this.setFoundMatchSubject = function (masterSubjectData) {
    foundMatchSubject = masterSubjectData;
  };

  this.getFoundMatchSubject = function () {
    return foundMatchSubject;
  };

  this.selectAccount = function (account) {
    selectedAccount = account;
    this.updateStorage();
  };

  this.getSelectedAccount = function () {
    return selectedAccount;
  };

  this.allSubjectsMatchingEvent = async function (currentConditions) {
    const subjects = this.getSortedVerifiedAndJobSubjects().filter((subject) => subject.id !== selectedSubject.id);
    await $q.all(subjects.map((subject) => this.attachMasterEventsToSubject(subject)));
    return subjects.every((subject) => {
      if (subject.masterEvents) {
        return subject.masterEvents.some((event) => this.compareEventWithCurrentEvent(event, currentConditions));
      }
      return this.compareEventWithCurrentEvent(subject.currentEvent.event, currentConditions);
    });
  };

  this.compareEventWithCurrentEvent = function (event, currentConditions) {
    const activeSchoolYearDisplay = currentConditions.schoolYearDisplay;
    const activeSellingMethodName = currentConditions.sellingMethodName;
    const activeSubProgramName = currentConditions.subProgramName;

    const targetSchoolYearDisplay = event.schoolYearDisplay;
    const targetSellingMethodName = event.eventType?.sellingMethodName || event.sellingMethod;
    const targetSubProgramName = event.eventType?.subProgramName || event.jobName;
    return (
      targetSchoolYearDisplay === activeSchoolYearDisplay &&
      targetSellingMethodName.toUpperCase() === activeSellingMethodName.toUpperCase() &&
      targetSubProgramName.toUpperCase() === activeSubProgramName.toUpperCase()
    );
  };

  this.setSelectedSubject = function (subjectData) {
    if (subjectData.studentId) {
      selectedSubject = this.getVerifiedSubject(subjectData.lifetouchId, subjectData.studentId);
    } else if (subjectData.masterSubjectId) {
      selectedSubject = this.getVerifiedSubjectByMasterSubjectId(subjectData.masterSubjectId);
    } else {
      selectedSubject = this.findSubjectByJob(subjectData.currentEvent.event, subjectData);
    }
    this.updateStorage();
  };

  this.attachMasterEventsToSubject = (subject, failSilently) => {
    return this.loadMasterSubjectEvents(subject, failSilently)
      .then(() => {
        subject.previewImageUrl = this.getLatestPreviewImageUrlFromMasterEvents(subject);
        subject.bearerTokenURL = this.getBearerTokenURLFromMasterEvents(subject);
      })
      .catch(() => {
        subject.previewImageUrl = null;
        subject.bearerTokenURL = null;
      });
  };

  this.loadMasterSubjectEvents = (masterSubject, failSilently) => {
    if (this.hasEventsLoaded(masterSubject)) {
      return $q.resolve();
    }

    masterSubject.eventLoading._promise =
      this._getEventLoadingPromise(masterSubject) ??
      (masterSubject.ltMasterSubjectUuid
        ? this._loadByMasterSubjectUuid(masterSubject)
        : this._loadBySubjectDetails(masterSubject, failSilently));

    return masterSubject.eventLoading._promise;
  };

  this._loadByMasterSubjectUuid = (masterSubject) =>
    this.masterSubjectService()
      .getMasterSubjectById(masterSubject.ltMasterSubjectUuid)
      .then((data) => this._handleEventLoadingStatus(masterSubject, data))
      .then((masterSubjectData) => {
        Object.assign(masterSubject, {
          imageAuthorizationUrl: masterSubjectData.imageAuthorizationUrl,
          bearerTokenURL: masterSubjectData.bearerTokenURL,
          masterEvents: MasterEventConsolidationService.consolidateEvents(masterSubjectData.masterEvents),
          lifetouchId: masterSubjectData.lifetouchId,
          studentId: masterSubjectData.studentId
        });
        return masterSubjectData;
      });

  this._loadBySubjectDetails = (masterSubject, failSilently) => {
    const blockService = $injector.get('BlockService');
    blockService.blockUI();
    return this.masterSubjectService()
      .getSubjectDetails(
        masterSubject.account.lifetouchId,
        masterSubject.studentId,
        masterSubject.subjectLastName,
        'GNA',
        failSilently
      )
      .then((data) => this._handleEventLoadingStatus(masterSubject, data))
      .then((masterSubjectData) => {
        Object.assign(masterSubject, {
          imageAuthorizationUrl: masterSubjectData.imageAuthorizationUrl,
          bearerTokenURL: masterSubjectData.bearerTokenURL,
          masterEvents: MasterEventConsolidationService.consolidateEvents(masterSubjectData.masterEvents)
        });
        return masterSubjectData;
      })
      .finally(() => {
        blockService.unblockUI();
      });
  };

  this._handleEventLoadingStatus = (masterSubject, data) => {
    const masterSubjectData = data?.masterSubject;

    this.setEventLoadingStatus(masterSubject, masterSubjectData ? 'resolved' : 'error');

    return masterSubjectData ?? $q.reject(data);
  };

  this.masterSubjectService = () => $injector.get('MasterSubjectService');

  this.loadMasterSubjects = function () {
    const masterSubjectService = $injector.get('MasterSubjectService');
    return masterSubjectService.getSubjectDetailsByUser().then((data = []) => {
      if (data?.error?.code === 'NOT_FOUND') {
        return { error: 'NOT_FOUND' };
      }
      data.forEach((subject) => {
        const userSubject = {
          masterSubjectId: subject.id,
          ltMasterSubjectUuid: subject.ltMasterSubjectUuid,
          subjectFirstName: subject.firstName,
          subjectLastName: subject.lastName,
          profileXrefStatus: subject.profileXrefStatus,
          currentGrade: subject.currentGrade
        };
        this.setEventLoadingStatus(userSubject, 'idle');
        this.addMasterSubject(userSubject);
      });
    });
  };

  this.addMasterSubject = function (masterSubjectData) {
    var foundMasterSubject = this.getVerifiedSubjectByMasterSubjectId(masterSubjectData.masterSubjectId);
    if (!foundMasterSubject) {
      this.setIdToSubject(masterSubjectData);
      verifiedSubjects.push(masterSubjectData);
      this.updateStorage();
    }
  };

  this.getSelectedSubject = function (select = true) {
    if (!selectedSubject && select) {
      var allSubjects = this.getSortedVerifiedAndJobSubjects();
      selectedSubject = allSubjects.length > 0 ? allSubjects[0] : undefined;
    }
    return selectedSubject;
  };

  this.getSortedVerifiedAndJobSubjects = function () {
    var allSubjects = this.getVerifiedAndJobSubjects();
    if (!allSubjects.length) {
      return [];
    }
    var first = [allSubjects[0]];
    var rest = allSubjects.slice(1);
    var sortedRest = R.sortBy(
      R.compose(R.toLower, function (subject) {
        return subject.subjectFirstName + subject.subjectLastName;
      })
    )(rest);

    return R.concat(first, sortedRest);
  };

  this.getVerifiedAndJobSubjects = function () {
    const verified = this.getVerifiedSubjects();

    const verifiedSubjectIds = new Set(verified.filter((vs) => vs.subjectOrderId).map((vs) => vs.subjectOrderId));

    return [
      ...this.getSubjectsByJob().filter((subject) => subject.id && !verifiedSubjectIds.has(subject.id)),
      ...verified
    ];
  };

  this.reset = function () {
    selectedAccount = null;
    selectedSubject = undefined;
    foundMatchSubject = null;
    this.updateStorage();
  };

  this.addVerifiedSubject = function (masterSubjectData) {
    var foundMasterSubject = this.getVerifiedSubject(masterSubjectData.lifetouchId, masterSubjectData.studentId);
    if (!foundMasterSubject) {
      this.setIdToSubject(masterSubjectData);
      verifiedSubjects.push(masterSubjectData);
      this.updateStorage();
    } else {
      if (this.needsEventsLoad(foundMasterSubject) && this.hasEventsLoaded(masterSubjectData)) {
        foundMasterSubject.masterEvents = masterSubjectData.masterEvents;
        this.setEventLoadingStatus(foundMasterSubject, 'resolved');
      }
    }
  };

  this.addOrUpdateSubjectByJob = function (job, subject) {
    const subjectByJobPresent = this.findSubjectByJob(job, subject);
    if (!subjectByJobPresent) {
      if (JobStore.isReorder() || JobStore.isProofOrder()) {
        this.firstLook = LooksStore.getFirstLook();
        var previewImageLocation = this.firstLook ? LooksStore.getPreviewImageLocation(this.firstLook) : null;
        subject.previewImageUrl = previewImageLocation;
        subject.bearerTokenURL = subject.bearerTokenURL || (selectedSubject && selectedSubject.bearerTokenURL) || '';
      }
      const jobSubject = {
        id: subject.id,
        lifetouchId: job.lifetouchId,
        subjectFirstName: subject.subjectFirstName,
        subjectLastName: subject.subjectLastName,
        subjectGrade: subject.subjectGrade,
        subjectRelationship: subject.subjectRelationship,
        subjectDetailsDeferred: subject.subjectDetailsDeferred,
        dontKnowTeacherName: subject.dontKnowTeacherName === undefined ? true : subject.dontKnowTeacherName,
        subjectTeacherName: subject.subjectTeacherName,
        subjectOrderId: subject.id,
        previewImageUrl: subject.previewImageUrl ? subject.previewImageUrl : null,
        imageAuthorizationUrl: '',
        bearerTokenURL: subject.bearerTokenURL || '',
        account: {
          accountAlias: job.accountName,
          lifetouchId: job.lifetouchId,
          city: job.jobCity,
          state: {
            country: '',
            name: job.jobState,
            description: ''
          },
          isFindMyPictureDay: false,
          studentIdSelected: false
        }
      };
      jobSubject.currentEvent = {
        accessCode: job.accessCode,
        portraitId: job.portraitId,
        event: job
      };
      this.setIdToSubject(jobSubject);
      subjectsByJob.push(jobSubject);
      this.setSelectedSubject(jobSubject);
    } else {
      this.updateSubjectByJob(job, subject);
    }
  };

  this.updateSubjectByJob = function (job, subject) {
    var existingSubject;

    if (subject.studentId) {
      existingSubject = this.getVerifiedSubject(job.lifetouchId, subject.studentId);
    } else if (subject.masterSubjectId) {
      existingSubject = this.getVerifiedSubjectByMasterSubjectId(subject.masterSubjectId);
    } else {
      existingSubject = this.findSubjectByJob(job, subject);
    }

    if (existingSubject) {
      existingSubject.subjectRelationship = subject.subjectRelationship;
      existingSubject.subjectGrade = subject.subjectGrade;
      existingSubject.dontKnowTeacherName =
        subject.dontKnowTeacherName === undefined ? true : subject.dontKnowTeacherName;
      existingSubject.subjectTeacherName = subject.subjectTeacherName;
      existingSubject.subjectOrderId = subject.id;
      if (subject.bearerTokenURL) {
        existingSubject.bearerTokenURL = subject.bearerTokenURL;
      }
      if (subject.previewImageUrl) {
        existingSubject.previewImageUrl = subject.previewImageUrl;
      }

      existingSubject.currentEvent = {
        accessCode: job.accessCode,
        portraitId: job.portraitId,
        event: job
      };
      this.setSelectedSubject(existingSubject);
      this.updateStorage();
    }
  };

  this.studentBannerSyncupWithCart = function (job, cartSubject) {
    if (cartSubject.subjectAccountConfirmed) {
      // As we have mapping with orderId between banner students and cart subjects,
      // looking banner subject based on orderId, if not found, else block will be executed to
      // find the banner student based on job details and does update the banner student
      const subject = this.findSubjectBySubjectOrderId(cartSubject.id);
      if (subject) {
        this.setSelectedSubject(subject);
      } else {
        this.updateSubjectByJob(job, cartSubject);
      }
    }
  };

  this.findSubjectBySubjectOrderId = function (id) {
    return R.find(function (subject) {
      return subject.subjectOrderId === id;
    })(this.getVerifiedAndJobSubjects());
  };

  this.updateSubjectDetails = (subjectsData) => {
    subjectsData.forEach((targetSubject) => {
      this.updateSelectedSubjectDetails(targetSubject);
      const existingSubject = this.findSubjectBySubjectOrderId(targetSubject.id);
      if (!existingSubject) return;

      existingSubject.subjectFirstName = targetSubject.subjectFirstName;
      existingSubject.subjectLastName = targetSubject.subjectLastName;
      existingSubject.subjectGrade = targetSubject.subjectGrade;
      existingSubject.subjectRelationship = targetSubject.subjectRelationship;
    });

    this.updateStorage();
  };

  this.updateSelectedSubjectDetails = (targetSubject) => {
    if (selectedSubject?.id !== targetSubject.id) {
      return;
    }
    selectedSubject.subjectFirstName = targetSubject.subjectFirstName;
    selectedSubject.subjectLastName = targetSubject.subjectLastName;
    selectedSubject.subjectGrade = targetSubject.subjectGrade;
    selectedSubject.subjectRelationship = targetSubject.subjectRelationship;
  };

  this.findSubjectById = function (id) {
    return this.getVerifiedAndJobSubjects().find((subject) => subject.id === id);
  };

  this.getSubjectsByJob = function () {
    return subjectsByJob;
  };

  this.findSubjectByJob = function (job, subject) {
    return R.find(function (existingSubject) {
      if (job.portraitId && job.accessCode) {
        return (
          existingSubject.currentEvent.portraitId === job.portraitId &&
          existingSubject.currentEvent.accessCode === job.accessCode &&
          existingSubject.subjectLastName === subject.subjectLastName
        );
      } else {
        return (
          existingSubject.currentEvent.event.jobKey === job.jobKey &&
          existingSubject.subjectFirstName === subject.subjectFirstName &&
          existingSubject.subjectLastName === subject.subjectLastName
        );
      }
    }, subjectsByJob);
  };

  this.sortVerifiedSubject = function () {
    verifiedSubjects = R.sortBy(R.compose(R.toLower, R.prop('subjectFirstName')))(this.getVerifiedSubjects());
  };

  this.getVerifiedSubjects = function () {
    return verifiedSubjects;
  };

  this.getVerifiedSubjectsCount = function () {
    return verifiedSubjects.length;
  };

  this.getVerifiedSubject = function (lifetouchId, studentId) {
    return R.find(function (subject) {
      return (
        subject.lifetouchId &&
        subject.lifetouchId.toString() === lifetouchId.toString() &&
        subject.studentId &&
        subject.studentId.toString() === studentId.toString()
      );
    })(verifiedSubjects);
  };

  this.getVerifiedSubjectByMasterSubjectId = function (masterSubjectId) {
    return verifiedSubjects.find((subject) => subject?.masterSubjectId.toString() === masterSubjectId.toString());
  };

  this.getVerifiedStudentIndex = function (lifetouchId, studentId) {
    if (!lifetouchId || !studentId) {
      return -1;
    }
    return R.findIndex(function (subject) {
      return (
        subject.lifetouchId &&
        subject.lifetouchId.toString() === lifetouchId.toString() &&
        subject.studentId &&
        subject.studentId.toString() === studentId.toString()
      );
    }, verifiedSubjects);
  };

  this.removeVerifiedSubject = function (subject) {
    var index = this.getVerifiedStudentIndex(subject.lid, subject.studentId);
    if (index > -1) {
      verifiedSubjects = R.remove(index, 1, verifiedSubjects);
      selectedSubject = !!verifiedSubjects ? verifiedSubjects[0] : undefined;
    }
  };

  this.removeSubject = function (subject) {
    var index;
    var isSelectedSubject = false;
    if (subject.ltMasterSubjectUuid) {
      index = R.findIndex(function (s) {
        return s.ltMasterSubjectUuid.toString() === subject.ltMasterSubjectUuid.toString();
      }, verifiedSubjects);
      if (index > -1) {
        verifiedSubjects = R.remove(index, 1, verifiedSubjects);
        isSelectedSubject = selectedSubject && selectedSubject.ltMasterSubjectUuid === subject.ltMasterSubjectUuid;
      }
    } else {
      index = R.findIndex(function (s) {
        return s.subjectOrderId.toString() === subject.subjectOrderId.toString();
      }, subjectsByJob);
      if (index > -1) {
        subjectsByJob = R.remove(index, 1, subjectsByJob);
        isSelectedSubject = selectedSubject && selectedSubject.subjectOrderId === subject.subjectOrderId;
      }
    }
    return isSelectedSubject;
  };

  this.getLatestPreviewImageUrlFromMasterEvents = function (masterSubject) {
    // Mirror SubjectLookResolver's master-event rule: skip group-only capture sessions
    // so this denormalized value can't surface a portrait the resolver would reject.
    return (
      masterSubject.masterEvents?.find(
        (masterEvent) => masterEvent.subjectInfo?.previewImageUrl && !masterEvent.subjectInfo?.isGroupOnlyCaptureSession
      )?.subjectInfo?.previewImageUrl ?? null
    );
  };

  // Source of the denormalized subject.bearerTokenURL (set by attachMasterEventsToSubject).
  // Picks the first master event carrying a token; site #1 stamps the same
  // master-subject imageAuthorization token onto every event, so any event will do.
  this.getBearerTokenURLFromMasterEvents = function (masterSubject) {
    const event = (masterSubject.masterEvents || []).find((e) => e?.subjectInfo?.bearerTokenURL);
    return event?.subjectInfo?.bearerTokenURL || null;
  };

  // First fallback rung for SubjectLookResolver's token: the selected subject's
  // denormalized token (site #1, from getBearerTokenURLFromMasterEvents), else the
  // active job's imageAuthorization token (site #2 / reorder, via JobStore.getJob()).
  // Both are the same session-scoped IRIS ImageAuthorization token in practice.
  this.getBearerTokenURL = function () {
    const subject = this.getSelectedSubject();
    return subject?.bearerTokenURL || JobStore.getJob()?.imageAuthorization?.bearerTokenURL;
  };

  this.clearAll = function () {
    verifiedSubjects = [];
    selectedSubject = null;
    subjectsByJob = [];
    subjectsLoading$ = new ReplaySubject();
    studentPlaceholderCount = 0;
    this.reset();
  };

  this.updateStorage = function () {
    StorageService.setData('subjects', {
      selectedAccount: selectedAccount,
      selectedSubject: selectedSubject,
      verifiedSubjects: verifiedSubjects,
      subjectsByJob: subjectsByJob,
      studentPlaceholderCount: studentPlaceholderCount
    });
  };

  this.removeSubjectByJob = function (lifetouchId) {
    var index = R.findIndex(function (subject) {
      return subject.lifetouchId.toString() === lifetouchId.toString();
    }, subjectsByJob);
    subjectsByJob.splice(index, 1);
  };

  this.isRecentJobInAnyVerifiedStudent = function (orderId) {
    return !!R.find(function (student) {
      return student.subjectOrderId === orderId;
    })(verifiedSubjects);
  };

  this.setSubjectClaimed = function (masterSubjectId) {
    var verifiedSubject = this.getVerifiedSubjectByMasterSubjectId(masterSubjectId);
    if (verifiedSubject) {
      verifiedSubject.unclaimed = false;
      this.updateStorage();
    }
  };

  this.setIdToSubject = function (subject) {
    if (!subject.id) {
      subject.id = GuidGenerator.generateGuid();
    }
  };

  this.isMasterSubjectProfileXrefStatusVerified = function () {
    return selectedSubject?.profileXrefStatus === MasterSubjectUserProfileXrefStatusConstants.VERIFIED;
  };

  this.onDeserialized = function (type, data) {
    if (!!data && !!data.subjects) {
      studentPlaceholderCount = data.subjects.studentPlaceholderCount;
      selectedSubject = data.subjects.selectedSubject;
      const selectedId = selectedSubject && selectedSubject.subjectOrderId;
      selectedAccount = data.subjects.selectedAccount;
      selectedMasterEvent = selectedSubject?.selectedEvent;
      verifiedSubjects = data.subjects.verifiedSubjects;
      subjectsByJob = R.sort(function (a, b) {
        return a.subjectOrderId === selectedId ? -1 : b.subjectOrderId === selectedId ? 1 : 0;
      }, data.subjects.subjectsByJob);
    }
  };

  this.needsEventsLoad = (subject) => !this.hasEventsLoaded(subject);

  this.hasEventsLoaded = (subject) => !subject || !subject.eventLoading || subject?.eventLoading.status === 'resolved';

  this.hasEventsLoadingFailed = (subject) => subject?.eventLoading?.status === 'error';

  this.setEventLoadingStatus = (subject = {}, status) => {
    Object.assign(subject, {
      eventLoading: { status }
    });
  };

  this._getEventLoadingPromise = (subject) => {
    const promise = subject?.eventLoading?._promise;

    if (typeof promise?.then === 'function') {
      return promise;
    }
    return null;
  };

  this.generateFirstNamePlaceholder = () =>
    `${$filter('ltTranslateFilter')('FIRST_NAME_PLACEHOLDER')} ${++studentPlaceholderCount}`;

  this.isGeneratedFirstNamePlaceholder = (name) => {
    if (!name || typeof name !== 'string') {
      return false;
    }

    const placeholderPrefix = `${$filter('ltTranslateFilter')('FIRST_NAME_PLACEHOLDER')} `;
    const normalizedName = name.trim();

    if (!normalizedName.startsWith(placeholderPrefix)) {
      return false;
    }

    return /^\d+$/.test(normalizedName.slice(placeholderPrefix.length).trim());
  };

  SessionTimeoutAction.register(this.clearAll.bind(this));
  LogoutSuccessAction.register(this.clearAll.bind(this));
  DeserializeAction.register(this.onDeserialized.bind(this));
}

App.service('SubjectStore', [
  '$filter',
  'StorageService',
  'SessionTimeoutAction',
  'LogoutSuccessAction',
  'DeserializeAction',
  '$injector',
  '$q',
  'LooksStore',
  'JobStore',
  'GuidGenerator',
  'MasterSubjectUserProfileXrefStatusConstants',
  'MasterEventConsolidationService',
  SubjectStore
]);
App.run([
  'SubjectStore',
  function (SubjectStore) {
    // eagerly initialize the store so that event listeners are registered
  }
]);
